import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { HttpError } from 'app/helpers/httpError';
import { ExposedJobRetryFailedMessagesReq } from 'app/interfaces/requests/Exposed/exposedJobRetryFailedMessagesReq';
import { ExposedListJobMessagesPaginatedReq } from 'app/interfaces/requests/Exposed/exposedListJobMessagesPaginatedReq';
import { ExposedListJobsRes } from 'app/interfaces/responses/Exposed/exposedListJobsRes';
import { ExposedReadJobDetailsRes } from 'app/interfaces/responses/Exposed/exposedReadJobDetailsRes';
import { ListMessageLogPaginateRes } from 'app/interfaces/responses/List/listMessageLogPaginateRes';
import { HttpStatus } from 'app/interfaces/support/status/httpStatus';
import { AlertsService } from 'app/services/system/alerts/alerts.service';
import { LoaderService } from 'app/services/system/loader/loader.service';
import { environment } from 'environments/environment';
import { Observable, Subject } from 'rxjs';
import { TranslationService } from '../translation/translation.service';

@Injectable({
    providedIn: 'root',
})
export class JobsService {
    constructor(
        private http: HttpClient,
        private loaderService: LoaderService,
        private translationService: TranslationService,
        private alertsService: AlertsService,
        private router: Router,
        public dialog: MatDialog
    ) {}

    public getJobs(jobTypes: string[], showLoader: boolean = true): Observable<ExposedListJobsRes[]> {
        if (showLoader) {
            this.loaderService.changeActiveLoaders(1);
            this.loaderService.changeLoaderMessage(this.translationService.translatePlural('label_fetch_jobs'));
        }

        let subject = new Subject<ExposedListJobsRes[]>();
        this.http
            .get(environment.localApi + 'jobs', {
                params: new HttpParams().set('jobTypes', jobTypes.join(',')),
            })
            .subscribe({
                next: (res: HttpStatus<ExposedListJobsRes[]>) => {
                    subject.next(res.returnItem);
                },
                error: (error) => {
                    HttpError(this.alertsService, this.router, error, '', '', '', '');
                    subject.error(error);
                },
            })
            .add(() => {
                if (showLoader) {
                    this.loaderService.changeActiveLoaders(-1);
                }
            });
        return subject.asObservable();
    }

    public getJobDetails(jobId: number, showLoader: boolean = false, showError: boolean = true): Promise<ExposedReadJobDetailsRes> {
        if (showLoader) {
            this.loaderService.changeActiveLoaders(1);
        }

        return new Promise((resolve, reject) => {
            this.http
                .get(environment.localApi + 'jobs/' + jobId)
                .subscribe({
                    next: (res: HttpStatus<ExposedReadJobDetailsRes>) => {
                        resolve(res.returnItem);
                    },
                    error: (error) => {
                        if (showError) {
                            HttpError(this.alertsService, this.router, error, '', '', '', '');
                            reject(error);
                        } else {
                            console.log(error);
                        }
                    },
                })
                .add(() => {
                    if (showLoader) {
                        this.loaderService.changeActiveLoaders(-1);
                    }
                });
        });
    }

    public getJobMessages(
        jobId: number,
        req: ExposedListJobMessagesPaginatedReq,
        showLoader: boolean = true
    ): Promise<ListMessageLogPaginateRes[]> {
        if (showLoader) {
            this.loaderService.changeActiveLoaders(1);
            this.loaderService.changeLoaderMessage(this.translationService.translate('label_fetch_jobs_messages'));
        }

        return new Promise((resolve, reject) => {
            this.http
                .post(`${environment.localApi}jobs/${jobId}/Messages`, req)
                .subscribe({
                    next: (res: HttpStatus<ListMessageLogPaginateRes[]>) => {
                        resolve(res.returnItem);
                    },
                    error: (error) => {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                        reject(error);
                    },
                })
                .add(() => {
                    if (showLoader) {
                        this.loaderService.changeActiveLoaders(-1);
                    }
                });
        });
    }

    public rerunFailedJobMessages(jobId: number, messageLogIds: number[] = null): Observable<number[]> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage(this.translationService.translate('label_requeue_job_messages'));
        let req: ExposedJobRetryFailedMessagesReq = {
            messageLogIds: messageLogIds,
        };
        let subject = new Subject<number[]>();
        this.http
            .post(environment.localApi + 'jobs/' + jobId + '/retryFailedMessages', req)
            .subscribe({
                next: (res: HttpStatus<number[]>) => {
                    this.alertsService.changeAlertShowIcon(true);
                    this.alertsService.changeAlertSuccessMessage(`Vinnslur settar af stað aftur: ${res.returnItem.length}`);
                    this.alertsService.changeAlertSuccessShow(true);
                    subject.next(res.returnItem);
                },
                error: (error) => {
                    HttpError(this.alertsService, this.router, error, '', '', '', '');
                    subject.error(error);
                },
            })
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });
        return subject.asObservable();
    }
}
